import { type Currency, minorUnitFactor } from './currency.js';
import { CurrencyMismatchError, NegativeAmountError } from './errors.js';

export type Money = { readonly amountMinor: bigint; readonly currency: Currency };

export function money(amountMinor: bigint | number | string, currency: Currency): Money {
  return { amountMinor: BigInt(amountMinor), currency };
}

export function zero(currency: Currency): Money {
  return { amountMinor: 0n, currency };
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amountMinor: a.amountMinor + b.amountMinor, currency: a.currency };
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amountMinor: a.amountMinor - b.amountMinor, currency: a.currency };
}

export function negate(a: Money): Money {
  return { amountMinor: -a.amountMinor, currency: a.currency };
}

export function multiply(a: Money, quantity: number | bigint): Money {
  if (typeof quantity === 'number' && !Number.isInteger(quantity)) {
    throw new TypeError('multiply expects an integer quantity; use convertBetween for rates');
  }
  return { amountMinor: a.amountMinor * BigInt(quantity), currency: a.currency };
}

export function sum(items: readonly Money[], currency: Currency): Money {
  return items.reduce<Money>((acc, item) => add(acc, item), zero(currency));
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  if (a.amountMinor < b.amountMinor) return -1;
  if (a.amountMinor > b.amountMinor) return 1;
  return 0;
}

export function equals(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.amountMinor === b.amountMinor;
}

export function isZero(a: Money): boolean {
  return a.amountMinor === 0n;
}

export function isNegative(a: Money): boolean {
  return a.amountMinor < 0n;
}

export function assertNonNegative(a: Money): Money {
  if (isNegative(a)) throw new NegativeAmountError(a.amountMinor);
  return a;
}

function divideRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new RangeError('Division by zero');
  const negative = numerator < 0n !== denominator < 0n;
  const absNumerator = numerator < 0n ? -numerator : numerator;
  const absDenominator = denominator < 0n ? -denominator : denominator;
  const quotient = absNumerator / absDenominator;
  const remainder = absNumerator % absDenominator;
  const rounded = remainder * 2n >= absDenominator ? quotient + 1n : quotient;
  return negative ? -rounded : rounded;
}

/**
 * One row of `exchange_rates` as the converter needs it: TRY per one unit of
 * `currency`, as the `numeric(20,8)` string the column holds, and the bulletin
 * day it was published for. The rate is never a `number` — eight decimal places
 * do not survive a JS float, and `rateToBase` is parsed digit by digit.
 */
export type ConversionRate = {
  readonly currency: Currency;
  readonly rateToBase: string;
  readonly rateDate: string;
};

/** The currency `exchange_rates` quotes against, which therefore has no row. */
export const RATE_BASE_CURRENCY: Currency = 'TRY';

type ScaledRate = { readonly scaled: bigint; readonly scale: bigint };

const UNIT_RATE: ScaledRate = { scaled: 1n, scale: 1n };

function scaleRate(rate: ConversionRate | null): ScaledRate {
  if (rate === null) return UNIT_RATE;
  if (typeof rate.rateToBase !== 'string') {
    throw new RangeError('rateToBase must be a decimal string, never a number');
  }
  const text = rate.rateToBase.trim();
  if (!/^\d+(\.\d+)?$/.test(text)) {
    throw new RangeError(`rateToBase must be a positive decimal string, got ${rate.rateToBase}`);
  }
  const [whole = '0', fraction = ''] = text.split('.');
  const scaled = BigInt(`${whole}${fraction}`);
  if (scaled === 0n) throw new RangeError('rateToBase must be above zero');
  return { scaled, scale: 10n ** BigInt(fraction.length) };
}

function assertRateQuotes(rate: ConversionRate | null, currency: Currency): void {
  if (rate === null) {
    if (currency !== RATE_BASE_CURRENCY)
      throw new CurrencyMismatchError(RATE_BASE_CURRENCY, currency);
    return;
  }
  if (rate.currency !== currency) throw new CurrencyMismatchError(rate.currency, currency);
}

/**
 * The one currency converter. `from` quotes `amount`'s currency and `to` quotes
 * `target`; `null` on either side means TRY, which is the base and has no row.
 *
 * Both rates must carry the same `rate_date`: dividing one day's bulletin by
 * another's invents a cross rate nobody published — docs/decisions/0019-exchange-rate-source.md.
 *
 * The whole conversion is one integer division of
 * `minor × rateFrom × factorTo` by `rateTo × factorFrom`, rounded half-up
 * exactly once, so a two-leg conversion never rounds twice.
 */
export function convertBetween(
  amount: Money,
  from: ConversionRate | null,
  to: ConversionRate | null,
  target: Currency,
): Money {
  assertRateQuotes(from, amount.currency);
  assertRateQuotes(to, target);
  if (from !== null && to !== null && from.rateDate !== to.rateDate) {
    throw new RangeError(
      `A conversion mixes two rate days: ${from.currency} at ${from.rateDate} and ${to.currency} at ${to.rateDate}`,
    );
  }

  const source = scaleRate(from);
  const destination = scaleRate(to);
  const numerator =
    amount.amountMinor * source.scaled * destination.scale * minorUnitFactor(target);
  const denominator = destination.scaled * source.scale * minorUnitFactor(amount.currency);
  return { amountMinor: divideRoundHalfUp(numerator, denominator), currency: target };
}

/**
 * The remainder left after dividing `amount` by weight goes round-robin to the
 * shares that asked for a share at all. A zero-weight entry is in `weights` to
 * hold its place in the result — a share nobody is owed anything on, not a
 * tie-break candidate — so it is skipped here exactly as a negative total
 * refuses the whole call below: neither lets a share with no claim on the
 * amount end up a minor unit ahead of one that does.
 */
export function allocate(amount: Money, weights: readonly number[]): Money[] {
  const total = weights.reduce((acc, w) => acc + w, 0);
  if (total <= 0) throw new RangeError('allocate requires weights summing above zero');

  const shares: bigint[] = [];
  let distributed = 0n;
  for (const weight of weights) {
    const share =
      (amount.amountMinor * BigInt(Math.round(weight * 1e6))) / BigInt(Math.round(total * 1e6));
    shares.push(share);
    distributed += share;
  }

  const claimants = weights.reduce<number[]>((indices, weight, index) => {
    if (weight > 0) indices.push(index);
    return indices;
  }, []);

  let remainder = amount.amountMinor - distributed;
  const step = remainder < 0n ? -1n : 1n;
  for (let i = 0; remainder !== 0n; i = (i + 1) % claimants.length) {
    const index = claimants[i] as number;
    shares[index] = (shares[index] ?? 0n) + step;
    remainder -= step;
  }

  return shares.map((amountMinor) => ({ amountMinor, currency: amount.currency }));
}

export function toDecimalString(amount: Money): string {
  const factor = minorUnitFactor(amount.currency);
  const negative = amount.amountMinor < 0n;
  const abs = negative ? -amount.amountMinor : amount.amountMinor;
  const whole = abs / factor;
  const fraction = abs % factor;
  const digits = factor.toString().length - 1;
  const text =
    digits === 0 ? whole.toString() : `${whole}.${fraction.toString().padStart(digits, '0')}`;
  return negative ? `-${text}` : text;
}

/**
 * Refuses a fraction with more digits than the currency's minor unit rather
 * than truncating it — `'1.005'` for EUR is a rounding decision (to €1.00 or
 * €1.01) that this function must not make silently, per money.md. A caller
 * that reads an amount a human typed truncates or rounds explicitly first;
 * `parseDecimalToMinor` in `statement-profile.ts` is the one caller today, and
 * it already refuses the extra digit before calling this at all.
 */
export function fromDecimalString(value: string, currency: Currency): Money {
  const factor = minorUnitFactor(currency);
  const digits = factor.toString().length - 1;
  const negative = value.trim().startsWith('-');
  const [whole = '0', fraction = ''] = value.trim().replace('-', '').split('.');
  if (fraction.length > digits) {
    throw new RangeError(
      `fromDecimalString refuses '${value}' for ${currency}: ${fraction.length} fraction digits given, above its ${digits}`,
    );
  }
  const padded = fraction.padEnd(digits, '0');
  const minor = BigInt(whole) * factor + BigInt(padded || '0');
  return { amountMinor: negative ? -minor : minor, currency };
}
